import React from 'react';
import { calculatePercentage } from '../../../lib/utils/math';
import { Progress } from '../../../components/progress/Progress';

import styles from './stage-header.module.scss';
type StageHeaderProps = {
  suffix?: React.ReactNode;
  actions?: React.ReactNode;
  name: string;
  completed: number;
  total: number;
  isActive?: boolean;
  progressColor?: string;
  /**
   * The `completed/total` caption. On by default; the calendar's stage row header turns it off
   * and puts an "add a step" button in the `actions` slot instead. The `<Progress>` bar below
   * is unaffected either way, so progress stays readable there.
   */
  showCount?: boolean;
};

export const StageHeader: React.FC<StageHeaderProps> = ({
  suffix,
  actions,
  name,
  completed,
  total,
  isActive,
  progressColor = 'var(--color-secondary)',
  showCount = true,
}) => {
  const progress = calculatePercentage(completed, total);

  return (
    <div className={styles['container']}>
      <div className={styles['header']}>
        <div className={styles['title']} data-is-active={!!isActive}>
          {suffix}

          <h3>{name}</h3>
        </div>

        <div className={styles['header-progress']}>
          {showCount ? (
            <div className={styles['progress-indicator']}>
              {completed}/{total}
            </div>
          ) : null}

          {actions}
        </div>
      </div>

      <Progress percentage={progress} color={progressColor} />
    </div>
  );
};

/**
 * The stage's order badge. Exported and rendered by stage.tsx and the calendar
 * pages, so it stays a component rather than being inlined.
 */
export const StageNumber = ({
  isActive,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & { isActive?: boolean }) => (
  <div
    className={className ? `${styles['stage-number']} ${className}` : styles['stage-number']}
    data-is-active={!!isActive}
    {...props}
  />
);
